import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Car, Bell, ShieldCheck, AlertTriangle, ArrowRight, ChevronRight, Circle } from "lucide-react";
import { toast } from "sonner";

const MODE_LABELS = {
  parking: "Parking",
  emergency: "Emergency",
  child: "Child onboard",
  pet: "Pet onboard",
  valet: "Valet",
  ev: "EV charging",
};

const REASON_LABELS = {
  blocked: "Blocked parking",
  emergency: "Emergency",
  lights: "Lights left on",
  window: "Window open",
  tyre: "Flat tyre",
  scratch: "Scratch / damage",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [v, a, s] = await Promise.all([
        api.get("/vehicles"),
        api.get("/alerts"),
        api.get("/stats"),
      ]);
      setVehicles(v.data);
      setAlerts(a.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const score = stats?.civic_score ?? 50;

  return (
    <DashLayout
      title="Control room"
      action={
        <Link to="/dashboard/vehicles/new" className="btn btn-primary" data-testid="add-vehicle-button">
          <Plus size={16} /> Add vehicle
        </Link>
      }
    >
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <>
          {/* Bento grid: stats + civic + vehicles + alerts */}
          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            {/* Stats row */}
            <StatBlock label="Total alerts" value={stats?.total_alerts ?? 0} testId="stat-total" />
            <StatBlock label="Pending" value={stats?.pending ?? 0} accent="warning" testId="stat-pending" />
            <StatBlock label="Confirmed" value={stats?.confirmed ?? 0} accent="success" testId="stat-confirmed" />
            <StatBlock label="False reports" value={stats?.false_reports ?? 0} accent="danger" testId="stat-false" />

            {/* Civic score */}
            <div className="col-span-12 lg:col-span-8 card-flat p-6" data-testid="civic-score-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="label-eyebrow">Civic score</div>
                  <div className="font-display font-black text-5xl tracking-tight mt-2 tabular">{score}<span className="text-gray-400 text-2xl">/100</span></div>
                </div>
                <div className="text-right">
                  <ShieldCheck size={28} className="text-[#002FA7] ml-auto" />
                  <div className="text-xs text-gray-500 mt-2 max-w-[12rem]">Higher score = faster trust signals from scanners.</div>
                </div>
              </div>
              <div className="seg-bar" data-testid="civic-seg-bar">
                {Array.from({ length: 20 }).map((_, i) => (
                  <span key={i} className={i < Math.round(score / 5) ? (score < 40 ? "danger" : "on") : ""} />
                ))}
              </div>
              <div className="flex justify-between text-xs font-mono text-gray-500 mt-2">
                <span>0</span><span>50</span><span>100</span>
              </div>
            </div>

            {/* Tier */}
            <div className="col-span-12 lg:col-span-4 card-flat p-6 flex flex-col justify-between" data-testid="tier-card">
              <div>
                <div className="label-eyebrow">Plan</div>
                <div className="font-display font-black text-3xl mt-2 capitalize">{user?.tier || "free"}</div>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  Upgrade to Guard for WhatsApp escalation, voice alarm, and authorised drivers.
                </p>
              </div>
              <button className="btn btn-ghost text-sm mt-4" disabled data-testid="upgrade-button">
                Upgrade · coming soon <ArrowRight size={14} />
              </button>
            </div>

            {/* Vehicles list */}
            <section className="col-span-12 lg:col-span-7" data-testid="vehicles-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-2xl">Your vehicles</h2>
                <span className="font-mono text-xs text-gray-500">{vehicles.length} registered</span>
              </div>
              {vehicles.length === 0 ? (
                <div className="card-flat p-10 text-center" data-testid="vehicles-empty">
                  <Car size={36} className="mx-auto text-gray-400 mb-4" />
                  <div className="font-display font-bold text-xl mb-2">No vehicles yet</div>
                  <p className="text-gray-600 mb-6 text-sm">Add your first car. We'll generate a unique scannable card you can print and stick on your windscreen.</p>
                  <Link to="/dashboard/vehicles/new" className="btn btn-primary" data-testid="empty-add-vehicle">
                    <Plus size={16} /> Add first vehicle
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {vehicles.map((v) => <VehicleRow key={v.id} v={v} />)}
                </div>
              )}
            </section>

            {/* Alerts feed */}
            <section className="col-span-12 lg:col-span-5" data-testid="alerts-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-2xl">Recent alerts</h2>
                <span className="font-mono text-xs text-gray-500">{alerts.length} total</span>
              </div>
              {alerts.length === 0 ? (
                <div className="card-flat p-8 text-center" data-testid="alerts-empty">
                  <Bell size={28} className="mx-auto text-gray-400 mb-3" />
                  <div className="font-display font-bold text-lg mb-1">All quiet</div>
                  <p className="text-gray-600 text-sm">No one has scanned your card yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.slice(0, 8).map((a) => <AlertRow key={a.id} a={a} />)}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </DashLayout>
  );
}

function StatBlock({ label, value, accent, testId }) {
  const color =
    accent === "warning" ? "text-[#F59E0B]" :
    accent === "success" ? "text-[#10B981]" :
    accent === "danger"  ? "text-[#E63946]" :
    "text-[#111827]";
  return (
    <div className="col-span-6 lg:col-span-3 card-flat p-5" data-testid={testId}>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display font-black text-4xl tabular mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function VehicleRow({ v }) {
  return (
    <Link
      to={`/dashboard/vehicles/${v.id}`}
      className="card-flat card-hover p-5 flex items-center justify-between group"
      data-testid={`vehicle-row-${v.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#111827] text-white grid place-items-center">
          <Car size={20} />
        </div>
        <div>
          <div className="font-display font-bold text-lg">{v.nickname}</div>
          <div className="font-mono text-xs text-gray-500">{v.plate_number} · {v.card?.card_code}</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 border border-gray-200 text-xs font-mono">
          <Circle size={6} className="fill-[#002FA7] text-[#002FA7]" /> {MODE_LABELS[v.current_mode]}
        </span>
        <ChevronRight size={18} className="text-gray-400 group-hover:text-[#002FA7]" />
      </div>
    </Link>
  );
}

function AlertRow({ a }) {
  const conf = Math.round((a.ai_confidence || 0) * 100);
  const isPending = a.status === "pending";
  const isUrgent = a.reason === "emergency" || a.reason === "blocked";
  return (
    <Link
      to={`/dashboard/alerts/${a.id}`}
      className="card-flat p-4 flex items-center gap-4 hover:border-[#002FA7] transition-colors"
      data-testid={`alert-row-${a.id}`}
    >
      <div className={`w-10 h-10 grid place-items-center shrink-0 ${isUrgent ? "bg-[#E63946] text-white" : "bg-[#F9F9F8] border border-gray-200"}`}>
        {isUrgent ? <AlertTriangle size={18} /> : <Bell size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold truncate">{REASON_LABELS[a.reason]}</div>
        <div className="font-mono text-[11px] text-gray-500 truncate">{a.reference}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono font-bold tabular text-sm">{conf}%</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500">
          {isPending ? "pending" : a.status}
        </div>
      </div>
    </Link>
  );
}
